import { useMemo } from 'react';
import {
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  subWeeks,
  startOfWeek,
  endOfWeek,
  subYears,
  startOfYear,
  endOfYear
} from 'date-fns';

export interface DateRange {
  startDay: string;
  endDay: string;
}

export interface ComparisonPeriod extends DateRange {
  description: string;
}

export interface DatePreset {
  label: string;
  value: string;
  getRange: () => DateRange;
  comparisonPreset: string;
}

export interface DateRangePresetsResult {
  presets: DatePreset[];
  getPresetRange: (preset: string, year?: number) => DateRange | null;
  getComparisonPeriod: (primaryStart: string, primaryEnd: string) => ComparisonPeriod;
  getComparisonPresetMapping: (preset: string) => string;
  getQuarter: (quarter: 1 | 2 | 3 | 4, year: number) => DateRange;
  getHalf: (half: 1 | 2, year: number) => DateRange;
  getCalendarYear: (year: number) => DateRange;
}

/**
 * Custom hook for managing date range presets and calculations
 * Centralizes all date logic for the Reports page
 */
export const useDateRangePresets = (): DateRangePresetsResult => {
  // Helper functions for date ranges
  const getLastWeek = (): DateRange => {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    return {
      startDay: format(startOfWeek(lastWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      endDay: format(endOfWeek(lastWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd')
    };
  };

  const getLast2Weeks = (): DateRange => {
    const now = new Date();
    // Set to end of yesterday to get complete days
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    // Go back 14 days from yesterday
    const fourteenDaysAgo = new Date(yesterday);
    fourteenDaysAgo.setDate(yesterday.getDate() - 13); // -13 because yesterday is day 1 of the 14 days
    return {
      startDay: format(fourteenDaysAgo, 'yyyy-MM-dd'),
      endDay: format(yesterday, 'yyyy-MM-dd')
    };
  };

  const getLast30Days = (): DateRange => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    return {
      startDay: format(thirtyDaysAgo, 'yyyy-MM-dd'),
      endDay: format(now, 'yyyy-MM-dd')
    };
  };

  const getLastMonth = (): DateRange => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    return {
      startDay: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
      endDay: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
    };
  };

  const getThisYear = (): DateRange => {
    const now = new Date();
    return {
      startDay: format(startOfYear(now), 'yyyy-MM-dd'),
      endDay: format(endOfYear(now), 'yyyy-MM-dd')
    };
  };

  const getLastYear = (): DateRange => {
    const now = new Date();
    const lastYear = subYears(now, 1);
    return {
      startDay: format(startOfYear(lastYear), 'yyyy-MM-dd'),
      endDay: format(endOfYear(lastYear), 'yyyy-MM-dd')
    };
  };

  const getQuarter = (quarter: 1 | 2 | 3 | 4, year: number): DateRange => {
    const quarterStartMonth = (quarter - 1) * 3;
    const start = new Date(year, quarterStartMonth, 1);
    const end = new Date(year, quarterStartMonth + 3, 0);
    return {
      startDay: format(start, 'yyyy-MM-dd'),
      endDay: format(end, 'yyyy-MM-dd')
    };
  };

  const getHalf = (half: 1 | 2, year: number): DateRange => {
    const start = new Date(year, half === 1 ? 0 : 6, 1);
    const end = new Date(year, half === 1 ? 6 : 12, 0);
    return {
      startDay: format(start, 'yyyy-MM-dd'),
      endDay: format(end, 'yyyy-MM-dd')
    };
  };

  const getCalendarYear = (year: number): DateRange => {
    return {
      startDay: format(new Date(year, 0, 1), 'yyyy-MM-dd'),
      endDay: format(new Date(year, 11, 31), 'yyyy-MM-dd')
    };
  };

  // Memoized preset definitions
  const presets: DatePreset[] = useMemo(() => [
    {
      label: 'Last Week',
      value: 'lastWeek',
      getRange: getLastWeek,
      comparisonPreset: 'previousWeek'
    },
    {
      label: 'Last 2 Weeks',
      value: 'last2Weeks',
      getRange: getLast2Weeks,
      comparisonPreset: 'previous2Weeks'
    },
    {
      label: 'Last 30 Days',
      value: 'last30Days',
      getRange: getLast30Days,
      comparisonPreset: 'previous30Days'
    },
    {
      label: 'Last Month',
      value: 'lastMonth',
      getRange: getLastMonth,
      comparisonPreset: 'previousMonth'
    },
    {
      label: 'This Year',
      value: 'thisYear',
      getRange: getThisYear,
      comparisonPreset: 'lastYear'
    },
    {
      label: 'Last Year',
      value: 'lastYear',
      getRange: getLastYear,
      comparisonPreset: 'lastYear_minus1'
    }
  ], []);

  /**
   * Calculate comparison period based on primary date range
   */
  const getComparisonPeriod = (primaryStart: string, primaryEnd: string): ComparisonPeriod => {
    // Validate input dates
    if (!primaryStart || !primaryEnd) {
      return { startDay: '', endDay: '', description: '' };
    }

    const start = new Date(primaryStart);
    const end = new Date(primaryEnd);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { startDay: '', endDay: '', description: '' };
    }

    const duration = end.getTime() - start.getTime();
    const durationDays = Math.round(duration / (24 * 60 * 60 * 1000));

    const comparisonEnd = new Date(start.getTime() - 24 * 60 * 60 * 1000); // One day before primary start
    const comparisonStart = new Date(comparisonEnd.getTime() - duration);

    // Create description
    const description = `Previous ${durationDays} day${durationDays !== 1 ? 's' : ''} (${format(comparisonStart, 'MMM d')} – ${format(comparisonEnd, 'MMM d')})`;

    return {
      startDay: format(comparisonStart, 'yyyy-MM-dd'),
      endDay: format(comparisonEnd, 'yyyy-MM-dd'),
      description
    };
  };

  /**
   * Get the preset mapping for comparison periods
   */
  const getComparisonPresetMapping = (preset: string): string => {
    const mapping: Record<string, string> = {
      'lastWeek': 'previousWeek',
      'last2Weeks': 'previous2Weeks',
      'last30Days': 'previous30Days',
      'lastMonth': 'previousMonth',
      'q1': 'q1',  // Same quarter, previous year (handled by year change)
      'q2': 'q2',
      'q3': 'q3',
      'q4': 'q4',
      '1h': '1h',  // Same half, previous year (handled by year change)
      '2h': '2h',
      'cy': 'cy'   // Calendar year, previous year (handled by year change)
    };
    return mapping[preset] || 'custom';
  };

  /**
   * Get date range for a specific preset
   */
  const getPresetRange = (preset: string, year?: number): DateRange | null => {
    const currentYear = year || new Date().getFullYear();

    switch (preset) {
      case 'lastWeek':
        return getLastWeek();
      case 'last2Weeks':
        return getLast2Weeks();
      case 'last30Days':
        return getLast30Days();
      case 'lastMonth':
        return getLastMonth();
      case 'q1':
        return getQuarter(1, currentYear);
      case 'q2':
        return getQuarter(2, currentYear);
      case 'q3':
        return getQuarter(3, currentYear);
      case 'q4':
        return getQuarter(4, currentYear);
      case '1h':
        return getHalf(1, currentYear);
      case '2h':
        return getHalf(2, currentYear);
      case 'cy':
        return getCalendarYear(currentYear);
      case 'previousWeek':
        // Get the week before last week
        const now = new Date();
        const twoWeeksAgo = subWeeks(now, 2);
        return {
          startDay: format(startOfWeek(twoWeeksAgo, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          endDay: format(endOfWeek(twoWeeksAgo, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        };
      case 'previous2Weeks':
        // Get the 14-day period before the last 14 days
        const now2 = new Date();
        const yesterday2 = new Date(now2);
        yesterday2.setDate(now2.getDate() - 1);
        // End of previous period is 15 days ago (day before start of last 14 days)
        const fifteenDaysAgo = new Date(yesterday2);
        fifteenDaysAgo.setDate(yesterday2.getDate() - 14);
        // Start is 28 days ago
        const twentyEightDaysAgo = new Date(yesterday2);
        twentyEightDaysAgo.setDate(yesterday2.getDate() - 27);
        return {
          startDay: format(twentyEightDaysAgo, 'yyyy-MM-dd'),
          endDay: format(fifteenDaysAgo, 'yyyy-MM-dd')
        };
      case 'previous30Days':
        // Get the 30-day period before the last 30 days
        const now3 = new Date();
        const sixtyDaysAgo = new Date(now3);
        sixtyDaysAgo.setDate(now3.getDate() - 60);
        const thirtyOneDaysAgo = new Date(now3);
        thirtyOneDaysAgo.setDate(now3.getDate() - 31);
        return {
          startDay: format(sixtyDaysAgo, 'yyyy-MM-dd'),
          endDay: format(thirtyOneDaysAgo, 'yyyy-MM-dd')
        };
      case 'previousMonth':
        // Get the month before last month
        const now4 = new Date();
        const twoMonthsAgo = subMonths(now4, 2);
        return {
          startDay: format(startOfMonth(twoMonthsAgo), 'yyyy-MM-dd'),
          endDay: format(endOfMonth(twoMonthsAgo), 'yyyy-MM-dd')
        };
      default:
        return null;
    }
  };

  return {
    presets,
    getPresetRange,
    getComparisonPeriod,
    getComparisonPresetMapping,
    getQuarter,
    getHalf,
    getCalendarYear
  };
};
